import React from 'react';
import Card from '../ui/Card';
import Button from '../ui/Button';
import SubscriptionStatus from './SubscriptionStatus';

/**
 * Reusable page header component with action section and subscription status
 *
 * @param {Object} props
 * @param {string} props.title - Main title text (e.g., "Ready to scrape Apollo.io?")
 * @param {string} props.description - Description text below the title
 * @param {string} props.buttonText - Text for the action button
 * @param {Function} props.onButtonClick - Click handler for the action button
 * @param {React.ReactNode} props.buttonIcon - Icon component to display in the button
 * @param {boolean} props.showButton - Whether to show the action button (default: true)
 * @param {Object} props.subscription - Subscription data object
 * @param {boolean} props.subscriptionLoading - Loading state for subscription
 * @param {string} props.gradientFrom - Starting color for gradient (default: '#f0f9ff')
 * @param {string} props.gradientTo - Ending color for gradient (default: 'white')
 * @param {string} props.borderColor - Border color for the card (default: 'var(--color-info-border)')
 */
const PageHeader = ({
  title,
  description,
  buttonText,
  onButtonClick,
  buttonIcon,
  showButton = true,
  subscription,
  subscriptionLoading,
  gradientFrom = '#f0f9ff',
  gradientTo = 'white',
  borderColor = 'var(--color-info-border)',
  variant = 'classic'
}) => {
  // "focused" layout: plain title on the app background, no action card and no
  // subscription panel - those figures live in the credits pill in UserLayout.
  if (variant === 'focused') {
    return (
      <div
        className="page-header"
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 16,
          flexWrap: 'wrap',
          marginBottom: 24
        }}
      >
        <div>
          <h1 style={{
            fontSize: 26,
            fontWeight: 800,
            color: 'var(--color-text-primary)',
            margin: 0,
            marginBottom: 6,
            letterSpacing: '-0.01em'
          }}>
            {title}
          </h1>
          {description && (
            <p style={{ color: 'var(--color-text-muted)', margin: 0, fontSize: 14 }}>
              {description}
            </p>
          )}
        </div>
        {showButton && buttonText && (
          <Button onClick={onButtonClick} icon={buttonIcon}>{buttonText}</Button>
        )}
      </div>
    );
  }

  return (
    <div className="page-header-grid" style={{
      display: 'grid',
      gridTemplateColumns: '2fr 1fr',
      gap: 24,
      marginBottom: 24
    }}>
      {/* Action Card */}
      <Card style={{
        background: `linear-gradient(135deg, ${gradientFrom} 0%, ${gradientTo} 100%)`,
        border: `1px solid ${borderColor}`
      }}>
        <Card.Content>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '12px'
          }}>
            <div>
              <h2 style={{
                fontSize: 20,
                fontWeight: 700,
                color: 'var(--color-info-fg)',
                margin: 0,
                marginBottom: 8
              }}>
                {title}
              </h2>
              <p style={{
                color: 'var(--color-text-muted)',
                margin: 0,
                fontSize: 14
              }}>
                {description}
              </p>
            </div>
            {showButton && (
              <Button
                onClick={onButtonClick}
                icon={buttonIcon}
              >
                {buttonText}
              </Button>
            )}
          </div>
        </Card.Content>
      </Card>

      {/* Subscription Status */}
      <SubscriptionStatus
        subscription={subscription}
        subscriptionLoading={subscriptionLoading}
      />
    </div>
  );
};

export default PageHeader;
